import { observer } from 'mobx-react';
import { useFormContext } from 'react-hook-form';
import { forwardRef, useEffect, useState } from 'react';
import {
  objectToCSVByKey,
  SkeletonBox,
  SnackbarApiErrorMessage,
  useDataProvider,
} from '@ui-framework/index';
import AutoComplete from './AutoComplete';

export const ServiceProviderLocationNumberSearchDisplay = async (
  searchedValues,
  serviceProviderId,
  provider,
  snackbar,
) => {
  const nameKey = 'locationName';
  const apiPath = `sxcl/v1/service-providers/${serviceProviderId}/locations`;
  const { get, getApiUrl } = provider;
  const response = await get(
    `${getApiUrl()}/${apiPath}?locationNumber=$in:${searchedValues}`,
  );
  if (response.ok) {
    const data = objectToCSVByKey(response?.data?.data, nameKey, true);
    return data;
  }
  snackbar.showMessage({
    type: 'error',
    data: <SnackbarApiErrorMessage errors={response?.errors} />,
  });
  return 'Error';
};

const ServiceProviderLocationNumber = forwardRef(({ field, ...props }, ref) => {
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);

  const nameKey = 'locationNumber';
  const valueKey = 'locationNumber';
  const searchKey = 'locationNumber';

  const { getValues, setValue } = useFormContext();
  const serviceProviderId = getValues('serviceProviderId');
  const apiPath = `service-providers/${serviceProviderId}/locations`;
  const currentValue = getValues(props?.name || 'undefined');
  const [loading, setLoading] = useState(true);

  const getEntities = async (searchValue = '') => {
    let result = null;
    if (!searchValue) {
      return null;
    }
    const searchQuery = `&${searchKey}=$in:${searchValue || ''}`;
    result = await get(
      `${getApiUrl()}/sxcl/v1/${apiPath}?limit=100${searchQuery}`,
    );
    if (result.ok) {
      const resultsData =
        result?.data?.data?.map(item => ({
          label: item[nameKey],
          value: item[valueKey],
        })) || [];
      return resultsData;
    }
    return null;
  };

  // Just change string values (csv) to objects
  const initialize = async () => {
    setLoading(true);
    const entitites = await getEntities(currentValue);
    if (entitites) {
      setValue(props.name, entitites, { shouldDirty: false });
    }
    setLoading(false);
  };

  useEffect(() => {
    // has no value
    if (!currentValue) {
      setValue(props?.name, null);
      setLoading(false);
    } else if (typeof currentValue === 'string' && currentValue) {
      initialize();
    } else {
      setLoading(false);
    }
  }, [currentValue]);

  if (loading) {
    return <SkeletonBox height="32px" />;
  }

  return (
    <AutoComplete
      ref={ref}
      nameKey={nameKey}
      valueKey={valueKey}
      apiPath={apiPath}
      searchKey={searchKey}
      field={field}
      isMulti
      apiVersion="2"
      {...props}
    />
  );
});

export default observer(ServiceProviderLocationNumber);
